import { generateError } from '../middleware';
import { useClient } from '../context/client';
import { useCallback } from 'react';

/**
 * Provides functions to handle CSV export jobs.
 * @returns {{
 *   startExport: (function({orderIds: Array<string>, columns: Array<string>}): Promise<string>),
 *   checkExportStatus: (function(string): Promise<Object>),
 *   getRecentJobs: (function(): Promise<Array<Object>>),
 *   getOrderCount: (function(string, string): Promise<{orderCount: number}>)
 * }}
 */
export function useOrderExport() {
    const client = useClient();
    
    const startExport = useCallback(async ({ orderIds, columns, startDate, endDate }) => {
        try {
            const response = await client.startCustomCsvExport({ orderIds, columns, startDate, endDate });
            return response;
        } catch (error) {
            console.error('Error starting order export:', error);
            throw error;
        }
    }, [client]);

    const checkExportStatus = useCallback(async (jobId) => {
        try {
            const status = await client.checkCustomCsvExportStatus(jobId);
            return status;
        } catch (error) {
            console.error('Error checking export status:', error);
            throw error;
        }
    }, [client]);

    const getRecentJobs = useCallback(async () => {
        try {
            const jobs = await client.getRecentCsvExportJobs();
            return jobs;
        } catch (error) {
            console.error('Error fetching recent jobs:', error);
            throw error;
        }
    }, [client]);

    const getOrderCount = useCallback(async (startDate, endDate) => {
        try {
            const response = await client.getOrderCountForDateRange(startDate, endDate);
            return response;
        } catch (error) {
            console.error('Error fetching order count:', error);
            throw error;
        }
    }, [client]);

    return { startExport, checkExportStatus, getRecentJobs, getOrderCount };
}